import {v4 as uuidv4} from "uuid"
import {BlockType, MarkdownAtom, MarkdownAtomType} from "../../model/markdown/MarkdownText"
import {ParsedBlock} from "./blockParseService"

interface LinkParseState {
    hasLink: boolean
    changedCharNo: number
    atom?: MarkdownAtom
}

class AtomParseService {
    getAtoms(block: ParsedBlock, initPosNo: number): Array<MarkdownAtom> {
        if (block.type === BlockType.TABLE) {
            return this.getTableAtoms(block, initPosNo)
        }
        let atoms: Array<MarkdownAtom> = []
        let posNo = initPosNo
        if (block.subBlocks.length === 0) {
            atoms = this.getAtomsFromContent(block.content, posNo, block.type)
        } else {
            for (let i = 0; i < block.subBlocks.length; i++) {
                const columnNo = block.subBlocks[i].offset
                atoms.push({
                    type: MarkdownAtomType.SUB_BLOCK,
                    checked: false,
                    columnNo: columnNo,
                    subAtoms: this.getAtomsFromContent(block.subBlocks[i].content, posNo + columnNo, block.type),
                    posNo: posNo,
                    id: uuidv4(),
                    content: '',
                    url: '',
                    indent: block.subBlocks[i].indent,
                })
                posNo += block.subBlocks[i].content.length + 1 + columnNo
            }
        }
        return atoms
    }

    getAtomsFromContent(content: string, posNo: number, type: BlockType): Array<MarkdownAtom> {
        const atoms: Array<MarkdownAtom> = []
        if (type === BlockType.CODE) {
            atoms.push({
                id: uuidv4(),
                type: MarkdownAtomType.PLAIN,
                content: content + '\n',
                posNo: posNo,
                columnNo: 0,
                url: '',
                subAtoms: [],
                checked: false,
                indent: 0
            })
            return atoms
        }
        let charNo = 0
        const subContent: Array<string> = ['']
        while (charNo < content.length) {
            const char = content[charNo]
            if (char === '*') {
                if (subContent[subContent.length - 1].length > 0 ) {
                    subContent.push('')
                }
                while (charNo < content.length && content[charNo] === '*') {
                    subContent[subContent.length - 1] += content[charNo]
                    charNo++
                }
                while (charNo < content.length && content[charNo] !== '*') {
                    subContent[subContent.length - 1] += content[charNo]
                    charNo++
                }
                while (charNo < content.length && content[charNo] === '*') {
                    subContent[subContent.length - 1] += content[charNo]
                    charNo++
                }
                subContent.push('')
            } else if (char === '[') {
                if (subContent[subContent.length - 1].length > 0 ) {
                    subContent.push('')
                }
                if (charNo + 2 < content.length && (content[charNo + 1] === ' ' || content[charNo + 1] === 'x') && content[charNo + 2] === ']' && (charNo + 3 >= content.length || content[charNo + 3] !== '(')) {
                    subContent[subContent.length - 1] += content[charNo]
                    subContent[subContent.length - 1] += content[charNo + 1]
                    subContent[subContent.length - 1] += content[charNo + 2]
                    charNo+=2
                    subContent.push('')
                } else {
                    const linkState = this.getParsedLink(content, charNo, posNo)
                    if (linkState.hasLink) {
                        subContent[subContent.length - 1] += content.substring(charNo, linkState.changedCharNo)
                        charNo = linkState.changedCharNo - 1
                        subContent.push('')
                    } else {
                        subContent[subContent.length - 1] += char
                    }
                }
            } else if (char === '`') {
                if (subContent[subContent.length - 1].length > 0 ) {
                    subContent.push('')
                }
                subContent[subContent.length - 1] += char
                charNo++
                while (charNo < content.length && content[charNo] !== '`') {
                    subContent[subContent.length - 1] += content[charNo]
                    charNo++
                }
                subContent.push('')
            } else {
                subContent[subContent.length - 1] += char
            }
            charNo++
        }
        let curPosNo = posNo
        for (let i = 0; i < subContent.length; i++) {
            const checkingContent = subContent[i]
            if (checkingContent.length === 0) {
                continue
            }
            const firstChar = checkingContent[0]
            if (firstChar === '*') {
                let start = 1
                let type = MarkdownAtomType.ITALIC
                if (checkingContent.length > 1 && checkingContent[1] === '*') {
                    start = 2
                    type = MarkdownAtomType.BOLD
                    if (checkingContent.length > 2 && checkingContent[2] === '*') {
                        start = 3
                        type = MarkdownAtomType.BOLD_ITALIC
                    }
                }
                atoms.push({
                    id: uuidv4(),
                    type: type,
                    content: checkingContent.substring(start, checkingContent.length - start),
                    posNo: curPosNo + start,
                    columnNo: curPosNo + start - posNo,
                    url: '',
                    subAtoms: [],
                    checked: false,
                    indent: 0,
                })
            } else if (firstChar === '~') {
                let start = 1
                let added = false
                if (checkingContent.length > 1 && checkingContent[1] === '~') {
                    start = 2
                    const type = MarkdownAtomType.LINE_THROUGH
                    added = true
                    atoms.push({
                        id: uuidv4(),
                        type: type,
                        content: checkingContent.substring(start, checkingContent.length - start),
                        posNo: curPosNo + start,
                        columnNo: curPosNo + start - posNo,
                        url: '',
                        subAtoms: [],
                        checked: false,
                        indent: 0,
                    })
                }
                if (!added) {
                    start = 0
                    atoms.push({
                        id: uuidv4(),
                        type: MarkdownAtomType.PLAIN,
                        content: checkingContent.substring(start, checkingContent.length - start),
                        posNo: curPosNo + start,
                        columnNo: curPosNo + start - posNo,
                        url: '',
                        subAtoms: [],
                        checked: false,
                        indent: 0,
                    })
                }
            } else if (firstChar === '`') {
                atoms.push({
                    id: uuidv4(),
                    type: MarkdownAtomType.CODE,
                    content: checkingContent.substring(1, checkingContent.length),
                    posNo: curPosNo + 1,
                    columnNo: curPosNo + 1 - posNo,
                    url: '',
                    subAtoms: [],
                    checked: false,
                    indent: 0,
                })
            } else if (firstChar === '[') {
                if (checkingContent.length === 3 && checkingContent[2] === ']' && (checkingContent[1] === ' ' || checkingContent[1] === 'x')) {
                    atoms.push({
                        id: uuidv4(),
                        type: MarkdownAtomType.CHECKBOX,
                        content: checkingContent[1],
                        posNo: curPosNo + 1,
                        columnNo: curPosNo + 1 - posNo,
                        url: '',
                        subAtoms: [],
                        checked: checkingContent[1] === 'x',
                        indent: 0,
                    })
                } else {
                    const linkState = this.getParsedLink(checkingContent, 0, 0)
                    if (linkState.hasLink && linkState.atom) {
                        atoms.push({
                            id: uuidv4(),
                            type: MarkdownAtomType.LINK,
                            content: linkState.atom.content,
                            posNo: curPosNo,
                            columnNo: curPosNo - posNo,
                            url: linkState.atom.url,
                            subAtoms: [],
                            checked: false,
                            indent: 0
                        })
                    } else {
                        atoms.push({
                            id: uuidv4(),
                            type: MarkdownAtomType.PLAIN,
                            content: checkingContent,
                            posNo: curPosNo,
                            columnNo: curPosNo - posNo,
                            url: '',
                            subAtoms: [],
                            checked: false,
                            indent: 0,
                        })
                    }
                }
            } else {
                atoms.push({
                    id: uuidv4(),
                    type: MarkdownAtomType.PLAIN,
                    content: checkingContent,
                    posNo: curPosNo,
                    columnNo: curPosNo - posNo,
                    url: '',
                    subAtoms: [],
                    checked: false,
                    indent: 0,
                })
            }
            curPosNo += checkingContent.length
        }
        return atoms
    }

    private getTableAtoms(block: ParsedBlock, initPosNo: number): Array<MarkdownAtom> {
        const atoms: Array<MarkdownAtom> = []
        let posNo = initPosNo

        for (const rowSubBlock of block.subBlocks) {
            const rowAtom: MarkdownAtom = {
                type: MarkdownAtomType.SUB_BLOCK,
                id: uuidv4(),
                posNo,
                columnNo: 0,
                content: rowSubBlock.hint,
                url: '',
                subAtoms: [],
                checked: false,
                indent: 0,
            }

            if (rowSubBlock.hint !== 'separator') {
                for (const cellSubBlock of rowSubBlock.subBlocks) {
                    const cellAtom: MarkdownAtom = {
                        type: MarkdownAtomType.SUB_BLOCK,
                        id: uuidv4(),
                        posNo: posNo + cellSubBlock.offset,
                        columnNo: cellSubBlock.offset,
                        content: cellSubBlock.content,
                        url: '',
                        subAtoms: this.getAtomsFromContent(
                            cellSubBlock.content,
                            posNo + cellSubBlock.offset,
                            BlockType.TABLE
                        ),
                        checked: false,
                        indent: 0,
                    }
                    rowAtom.subAtoms.push(cellAtom)
                }
            }

            atoms.push(rowAtom)
            posNo += rowSubBlock.content.length + 1
        }

        return atoms
    }

    isCheckBoxState(char: string): boolean {
        return char === ' ' || char == 'x'
    }

    getParsedLink(content: string, charNo: number, posNo: number): LinkParseState {
        let checkCharNo = charNo + 1
        let linkTitle = ''
        let titleDepth = 1
        while (checkCharNo < content.length) {
            const char = content[checkCharNo]
            if (char === '[') {
                titleDepth++
            } else if (char === ']') {
                titleDepth--
                if (titleDepth <= 0) {
                    checkCharNo++
                    break
                }
            }
            linkTitle += char
            checkCharNo++
        }
        if (titleDepth > 0 || content[checkCharNo] !== '(') {
            return {
                hasLink: false,
                changedCharNo: charNo
            }
        }
        let url = ''
        let urlDepth = 0
        while (checkCharNo < content.length) {
            const char = content[checkCharNo]
            if (char === '(') {
                urlDepth++
                if (urlDepth > 1) {
                    url += char
                }
            } else if (char === ')') {
                urlDepth--
                if (urlDepth <= 0) {
                    checkCharNo++
                    break
                }
                url += char
            } else {
                url += char
            }
            checkCharNo++
        }
        if (urlDepth > 0) {
            return {
                hasLink: false,
                changedCharNo: charNo
            }
        }
        return {
            hasLink: true,
            changedCharNo: checkCharNo,
            atom: {
                id: uuidv4(),
                type: MarkdownAtomType.LINK,
                content: linkTitle,
                url: url,
                subAtoms: [],
                posNo: posNo + charNo,
                columnNo: charNo,
                checked: false,
                indent: 0,
            }
        }
    }
}

export const atomParseService = new AtomParseService()
